// src/App.tsx
import React, { useEffect } from "react";
import "./App.css";
import "./global.scss";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Layout from "./Layout";
import Login from "./pages/login/Login";
import NotFound from "./pages/notFound/NotFound";
import { useNavigate } from "react-router-dom";
import Dashboard from "./pages/dashboard/Dashboard";
import Request from "./pages/request/Request";
import RequestDetails from "./pages/request/teacher/RequestDetails";
import TuitionClasses from "./pages/tuition-classes/TuitionClasses";
import TuitionPeriods from "./pages/tuition-periods/TuitionPeriods";
import TuitionPeriodDetails from "./pages/tuition-periods/TuitionPeriodDetails";
import TeacherList from "./pages/teacher/TeacherList";
import StudentList from "./pages/student/StudentList";
import ParentList from "./pages/parent/ParentList";
import ParentDetails from "./pages/parent/ParentDetails";
import { ToastProvider } from "./components/toasts/ToastContext";
import ToastInitializer from "./components/toasts/ToastInitializer";
import AdminList from "./pages/admin/AdminList";
import TeacherDetails from "./pages/teacher/TeacherDetails";
import CouponList from "./pages/coupon/CouponList";
import GuruCoin from "./pages/guru-coin/GuruCoin";
import GuruCoinTransaction from "./pages/transactions/guru-coin-transactions/GuruCoinTransaction";
import PaymentsPage from "./pages/transactions/payments/Payments";
import TuitionJobPostsPage from "./pages/tuition/job-posts/TuitionJobPosts";
import SlotIncreaseRequests from "./pages/tuition/slot-increase-requests/SlotIncreaseRequests";
import TermsConditionsForTeachers from "./pages/page-contents/terms-conditions/TermsConditionsForTeachers";
import TermsConditionsForParents from "./pages/page-contents/terms-conditions/TermsConditionsForParents";
import { get } from "./redux/ApiHelper";
import { AdminRole } from "./enums/admin-roles";
import { useDispatch, useSelector } from "react-redux";
import { setSignedAdmin, setAuthLoading } from "./redux/slices/AdminSlice";
import { RootState } from "./redux/store";
import PayoutsPage from "./pages/transactions/payouts/Payouts";
import PrivacyPolicy from "./pages/page-contents/terms-conditions/PrivacyPolicy";
import RefundPolicy from "./pages/page-contents/terms-conditions/RefundPolicy";
import TrainingVideo from "./pages/training-video/TraningVideo";
import BannerSettings from "./pages/banner-settings/BannerSettings";
import TrialClassPage from "./pages/trial-class/TrialClass";
import InfoSettingTabs from "./pages/info/InfoSettingTabs";
import GroupClassByTeacher from "./pages/group-class/GroupClassByTeacher";
import AdminPass from "./pages/admin-pass/AdminPass";
import { isPackageActive } from "./utils/helper.util";
import PurchasedPackageList from "./pages/purchased-package/PurchasedPackageList";
import TeacherShadowWallet from "./pages/teacher-shadow-wallet/TeacherShadowWallet";
import GroupClassTransaction from "./pages/transactions/GroupClassPayments";
import AccessDetails from "./pages/access-details/AccessDetails";
import FranchisePayment from "./pages/transactions/FranchisePayments";
import ContactAccessPayment from "./pages/transactions/payments/ContactAccessPayment";
import GeoCoordianteTierSetupSettings from "./pages/tier-settings/GeoCoordianteTierSetupSettings";
import Regular from "./pages/examination/Regular";
import CoinEarner from "./pages/examination/CoinEarner";
import Puzzle from "./pages/examination/Puzzle";
import Questions from "./pages/examination/Questions";
import MandatoryRequests from "./pages/examination/mandatory/Requests";
import MandatoryQuestionPapers from "./pages/examination/mandatory/MandatoryQuestionPapers";
import "./services/cometChat.service";
import CometChatUserHanlder from "./handlers/CometChatUserHanlder";
import Loader from "./components/loader/Loader";
import MapView from "./pages/map-view/MapView";
import LocationMapView from "./pages/map-view/LocationMapView";
import Chat from "./pages/chat/Chat";
import { SocketProvider } from "./context/SocketContext";
import AgreementRecords from "./pages/page-contents/agreement-records/AgreementRecords";
import PushNotification from "./pages/push-notification/PushNotification";

const useAuthCheck = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const token = localStorage.getItem("jwt");
    if (!token) {
      navigate("/login");
    } else {
      // You can add token validation or refresh logic here if necessary
    }
  }, [navigate]); // Trigger this effect whenever the component mounts

  useEffect(() => {
    const token = localStorage.getItem("jwt");
    const checkValidAccess = async () => {
      dispatch(setAuthLoading(true));
      await get("/admin/auth/valid-access")
        .then((response) => {
          dispatch(
            setSignedAdmin({
              email: response.email,
              role: response.role,
              type: response.type,
              name: response.name,
              phoneNumber: response.phoneNumber,
              accessValidity: response.accessValidity ?? "",
              id: response.id,
              franchisePackAmount: response.franchisePackAmount,
            })
          );
        })
        .catch((error) => {
          dispatch(setAuthLoading(false));
          localStorage.clear();
          navigate("/login");
          console.error("Error in accessing:", error);
        });
    };
    if (token) {
      checkValidAccess();
    } else {
      dispatch(setAuthLoading(false));
    }
  }, [dispatch, navigate]);
};

const routePaths: {
  path: string;
  component: React.ReactNode;
  role: AdminRole;
}[] = [
    {
      path: "/",
      component: <Dashboard />,
      role: AdminRole.ADMIN, //Both admin and super admin
    },
    {
      path: "/map-view",
      component: <MapView />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/map/location",
      component: <LocationMapView />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/chat",
      component: <Chat />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/teacher",
      component: <TeacherList />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/teacher/:id",
      component: <TeacherDetails />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/student",
      component: <StudentList />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/parent",
      component: <ParentList />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/parent/:id",
      component: <ParentDetails />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/tuition-requests",
      component: <Request />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/request/:id",
      component: <RequestDetails />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/tuition-periods",
      component: <TuitionPeriods />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/tuition-periods/:id",
      component: <TuitionPeriodDetails />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/tuition-classes",
      component: <TuitionClasses />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/admin",
      component: <AdminList />,
      role: AdminRole.SUPER_ADMIN,
    },
    {
      path: "/coupon",
      component: <CouponList />,
      role: AdminRole.SUPER_ADMIN, //only super admin access
    },
    {
      path: "/info-settings",
      component: <InfoSettingTabs />,
      role: AdminRole.SUPER_ADMIN,
    },
    {
      path: "/tier-settings",
      component: <GeoCoordianteTierSetupSettings />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/guru-coin",
      component: <GuruCoin />,
      role: AdminRole.SUPER_ADMIN,
    },
    {
      path: "/guru-coin-transaction",
      component: <GuruCoinTransaction />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/payments",
      component: <PaymentsPage />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/payouts",
      component: <PayoutsPage />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/tuition-job-posts",
      component: <TuitionJobPostsPage />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/terms-conditions/teachers",
      component: <TermsConditionsForTeachers />,
      role: AdminRole.SUPER_ADMIN,
    },
    {
      path: "/terms-conditions/parents",
      component: <TermsConditionsForParents />,
      role: AdminRole.SUPER_ADMIN,
    },
    {
      path: "/privacy-policy",
      component: <PrivacyPolicy />,
      role: AdminRole.SUPER_ADMIN,
    },
    {
      path: "/refund-policy",
      component: <RefundPolicy />,
      role: AdminRole.SUPER_ADMIN,
    },
    {
      path: "/agreement-records",
      component: <AgreementRecords />,
      role: AdminRole.SUPER_ADMIN,
    },
    {
      path: "/train-video",
      component: <TrainingVideo />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/banner",
      component: <BannerSettings />,
      role: AdminRole.SUPER_ADMIN,
    },
    {
      path: "/trial-class",
      component: <TrialClassPage />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/group-class",
      component: <GroupClassByTeacher />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/admin-pass",
      component: <AdminPass />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/purchased-package",
      component: <PurchasedPackageList />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/teachers-shadow-wallet",
      component: <TeacherShadowWallet />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/group-class-payments",
      component: <GroupClassTransaction />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/access-details",
      component: <AccessDetails />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/franchise-payments",
      component: <FranchisePayment />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/trial-class-payments",
      component: <GuruCoinTransaction initialTab="trial-payments" />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/contact-access-payments",
      component: <ContactAccessPayment />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/examination/regular",
      component: <Regular />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/examination/coin-earner",
      component: <CoinEarner />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/examination/puzzle",
      component: <Puzzle />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/examination/questions",
      component: <Questions />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/examination/mandatory/requests",
      component: <MandatoryRequests />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/examination/mandatory/papers",
      component: <MandatoryQuestionPapers />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/push-notification",
      component: <PushNotification />,
      role: AdminRole.ADMIN,
    },
    {
      path: "/slot-increase-requests",
      component: <SlotIncreaseRequests />,
      role: AdminRole.ADMIN,
    },
  ];

const App: React.FC = () => {
  useAuthCheck(); // Custom hook to check authentication and redirect if necessary
  const { signedAdmin, isAuthLoading } = useSelector((state: RootState) => state.admin);
  const token = localStorage.getItem("jwt");

  // Show loader while fetching admin data (only if token exists)
  if (isAuthLoading && token) {
    return <Loader />;
  }

  return (
    <div className="App">
      <SocketProvider>
        <ToastProvider>
          <ToastInitializer />
          <CometChatUserHanlder />
          <Routes>
          {/* All routes will be wrapped with Layout */}
          <Route element={<Layout />}>
            {routePaths.map((rp) => {
              const isSuperAdmin = signedAdmin.role === AdminRole.SUPER_ADMIN;
              const isAdmin = signedAdmin.role === AdminRole.ADMIN;
              const isFranchise = signedAdmin.type === "franchise";
              const isEmployee = signedAdmin.type === "employee";
              const isAccessValid = isPackageActive(signedAdmin.accessValidity);
              const isAdminPassOrRoot =
                rp.path === "/admin-pass" ||
                rp.path === "/" ||
                rp.path === "/teachers-shadow-wallet";

              // Super Admin can access all routes
              if (isSuperAdmin) {
                return (
                  <Route key={rp.path} path={rp.path} element={rp.component} />
                );
              }

              if (
                isAdmin &&
                rp.role === AdminRole.ADMIN &&
                isFranchise &&
                (rp.path === "/group-class" ||
                  rp.path === "/access-details" ||
                  rp.path === "/group-class-payments" ||
                  rp.path === "/franchise-payments")
              ) {
                return null;
              }
              // Admin (employee or valid franchise) can access only Admin routes
              if (
                isAdmin &&
                rp.role === AdminRole.ADMIN &&
                (isEmployee || (isFranchise && isAccessValid))
              ) {
                return (
                  <Route key={rp.path} path={rp.path} element={rp.component} />
                );
              }

              // Anyone can access "/admin-pass" or "/"
              if (isAdminPassOrRoot) {
                return (
                  <Route key={rp.path} path={rp.path} element={rp.component} />
                );
              }

              // Franchise with invalid package access gets redirected to "/admin-pass"
              if (isFranchise && !isAccessValid && !isAdminPassOrRoot) {
                return (
                  <Route key={rp.path} path={rp.path} element={<AdminPass />} />
                );
              }

              // No access
              return null;
            })}
          </Route>

          {/* Routes without layout */}
          <Route path="/login" element={<Login />} />

          {/* Catch-all Route for 404 */}
          <Route path="*" element={<NotFound />} />
          </Routes>
        </ToastProvider>
      </SocketProvider>
    </div>

  );
};

export default App;
